import type { ClipboardEventHandler } from "react";
import { Copy, Plus, Trash2 } from "lucide-react";
import type { ExpectedDebitsBandDraft, RelationClass } from "../../types";
import { RELATION_CLASSES } from "../../types";
import { Button } from "../Button";
import { IconButton } from "../IconButton";
import { inputClass } from "../fields/fieldStyles";
import { parsePastedBands } from "../../utils/workbooks";
import { RELATION_CLASS_LABELS } from "../../utils/labels";
import { compositeOfBands, BAND_FIELDS, type BandNumberField } from "../../utils/expectedDebitsBands";

interface ExpectedDebitsEditorProps {
  bands: readonly ExpectedDebitsBandDraft[];
  onChange: (bands: readonly ExpectedDebitsBandDraft[]) => void;
  children?: never;
}

function emptyBand(relationClass: RelationClass): ExpectedDebitsBandDraft {
  return { relationClass, ageStart: null, ageEnd: null, maleExpected: null, maleBase: null, femaleExpected: null, femaleBase: null, compositeExpected: null, compositeBase: null };
}

const HEAD = "px-2 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500";

/**
 * Employee, Spouse, and Child sections stacked on one page, as in the table
 * actuaries maintain today: each has its own age-band grid (Min Age, Max Age,
 * then Male, Female, and Composite Expected and Base), a composite line, an Add
 * band button, and tab-separated paste; Spouse has Copy Employee.
 */
export function ExpectedDebitsEditor({ bands, onChange }: ExpectedDebitsEditorProps) {
  const updateBand = (index: number, key: BandNumberField, raw: string) => {
    const parsed = raw.trim() === "" ? null : Number(raw);
    onChange(bands.map((band, candidate) => (candidate === index ? { ...band, [key]: parsed === null || Number.isFinite(parsed) ? parsed : band[key] } : band)));
  };
  const removeBand = (index: number) => onChange(bands.filter((_, candidate) => candidate !== index));
  const addBand = (relationClass: RelationClass) => onChange([...bands, emptyBand(relationClass)]);

  const pasteInto =
    (relationClass: RelationClass): ClipboardEventHandler<HTMLDivElement> =>
    (event) => {
      const text = event.clipboardData.getData("text/plain");
      if (!text.includes("\t") && !text.includes("\n")) return;
      event.preventDefault();
      const pasted = parsePastedBands(text, relationClass);
      if (pasted.length === 0) return;
      onChange([...bands.filter((band) => band.relationClass !== relationClass), ...pasted]);
    };

  const copyEmployeeToSpouse = () => {
    const employee = bands.filter((band) => band.relationClass === "Applicant").map((band) => ({ ...band, relationClass: "SpouseOrDependentAdult" as const }));
    onChange([...bands.filter((band) => band.relationClass !== "SpouseOrDependentAdult"), ...employee]);
  };

  return (
    <div className="space-y-6">
      <p className="text-xs text-gray-500">Type values or paste tab-separated rows from a spreadsheet into a section (Min Age, Max Age, then Male, Female, and Composite Expected and Base). The last band's Max Age can be 999 for "and older".</p>
      {RELATION_CLASSES.map((relationClass) => {
        const label = RELATION_CLASS_LABELS[relationClass];
        const rows = bands.map((band, index) => ({ band, index })).filter(({ band }) => band.relationClass === relationClass);
        const composite = compositeOfBands(rows.map(({ band }) => band));
        return (
          <section key={relationClass} aria-label={`${label} bands`} onPaste={pasteInto(relationClass)}>
            <div className="mb-2 flex items-center justify-between gap-3">
              <h3 className="text-sm font-semibold text-brand-navy">
                {label} <span className="font-normal text-gray-500">({rows.length} band{rows.length === 1 ? "" : "s"})</span>
              </h3>
              <span className="flex items-center gap-2">
                {relationClass === "SpouseOrDependentAdult" && (
                  <Button variant="secondary" size="sm" icon={Copy} onClick={copyEmployeeToSpouse} disabled={!bands.some((band) => band.relationClass === "Applicant")} title="Replace the Spouse bands with a copy of the Employee bands">
                    Copy Employee
                  </Button>
                )}
                <Button variant="secondary" size="sm" icon={Plus} onClick={() => addBand(relationClass)}>
                  Add band
                </Button>
              </span>
            </div>
            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="w-full text-sm">
                <caption className="sr-only">{label} age bands</caption>
                <thead className="bg-gray-50">
                  <tr>
                    {BAND_FIELDS.map((field) => (
                      <th key={field.key} scope="col" className={`${HEAD} ${field.group ? "" : ""}`}>
                        {field.label}
                      </th>
                    ))}
                    <th scope="col" className={HEAD}>
                      <span className="sr-only">Row actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 && (
                    <tr>
                      <td colSpan={BAND_FIELDS.length + 1} className="px-3 py-5 text-center text-sm text-gray-500">
                        No {label.toLowerCase()} bands yet. Add a band or paste rows from a spreadsheet.
                      </td>
                    </tr>
                  )}
                  {rows.map(({ band, index }) => (
                    <tr key={index} className="border-t border-gray-100">
                      {BAND_FIELDS.map((field) => (
                        <td key={field.key} className="px-1 py-1">
                          <label htmlFor={`band-${index}-${field.key}`} className="sr-only">
                            {field.label} for row {index + 1}
                          </label>
                          <input id={`band-${index}-${field.key}`} type="text" inputMode="decimal" value={band[field.key] ?? ""} onChange={(event) => updateBand(index, field.key, event.currentTarget.value)} className={`${inputClass("default", "sm")} w-24 text-right`} />
                        </td>
                      ))}
                      <td className="px-1 py-1">
                        <IconButton icon={Trash2} label={`Remove row ${index + 1}`} tone="danger" size="sm" onClick={() => removeBand(index)} />
                      </td>
                    </tr>
                  ))}
                </tbody>
                {rows.length > 0 && (
                  <tfoot className="border-t border-gray-200 bg-gray-50 text-xs">
                    <tr>
                      <th scope="row" colSpan={2} className="px-2 py-2 text-left font-semibold text-gray-600" title="Simple average of the bands above. The production formula (exposure-weighted, as in the current table) is still to be confirmed.">
                        Composite {label} <span className="font-normal text-gray-400">(simple average)</span>
                      </th>
                      {BAND_FIELDS.filter((field) => field.group !== "age").map((field) => (
                        <td key={field.key} className="px-3 py-2 text-right font-medium tabular-nums text-gray-700">
                          {composite[field.key] === null ? "–" : composite[field.key]?.toFixed(2)}
                        </td>
                      ))}
                      <td />
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </section>
        );
      })}
    </div>
  );
}
